import { VideoCamera, Calendar, CalendarCheck, Link } from '@phosphor-icons/react'
import TemplateInput from '@/components/ui/TemplateInput'
import { Select } from '@/components/ui/Select'
import type { WorkflowNodeData, MeetingProvider } from '@/types/workflow'

interface ScheduleMeetingConfigProps {
  data: WorkflowNodeData
  updateField: (field: string, value: any) => void
}

import {
  inputClass,
  inputStyle,
  sectionClass,
  infoBannerClass,
  checkboxInputClass,
  checkboxLabelClass,
} from './_kit'
import { Field } from '@/components/ui/Field'
const PROVIDERS: { value: MeetingProvider; label: string; icon: typeof VideoCamera; desc: string }[] = [
  { value: 'zoom', label: 'Zoom', icon: VideoCamera, desc: 'Create instant meeting link' },
  { value: 'google_calendar', label: 'Google Calendar', icon: Calendar, desc: 'Create calendar event' },
  { value: 'outlook_calendar', label: 'Outlook Calendar', icon: CalendarCheck, desc: 'Create calendar event' },
  { value: 'calendly', label: 'Calendly', icon: Link, desc: 'Attach booking link' },
]

const DEFAULT_CONFIG = {
  provider: 'zoom' as MeetingProvider,
  topic_template: '{{contact.company_name}} — Quick Chat',
  duration: 30,
  agenda_template: '',
  include_contact_as_attendee: true,
  calendar_id: 'primary',
  outlook_user_id: 'me',
}

const DURATION_OPTIONS = [
  { value: '15', label: '15 minutes' },
  { value: '30', label: '30 minutes' },
  { value: '45', label: '45 minutes' },
  { value: '60', label: '60 minutes' },
]

const HELP_TEXT: Record<MeetingProvider, string> = {
  zoom: 'Creates a Zoom meeting link and stores it on the contact for use in follow-up emails. Configure Zoom credentials in Settings → Integrations.',
  google_calendar: 'Creates a Google Calendar event with the contact as attendee. The event link is stored on the contact. Configure service account in Settings → Integrations.',
  outlook_calendar: 'Creates an Outlook Calendar event with the contact as attendee. Configure Microsoft credentials in Settings → Integrations.',
  calendly: 'Fetches your Calendly booking link and stores it on the contact so follow-up emails can include it. Configure Calendly token in Settings → Integrations.',
}

export default function ScheduleMeetingConfig({ data, updateField }: ScheduleMeetingConfigProps) {
  const config = { ...DEFAULT_CONFIG, ...data.schedule_meeting_config }

  const updateConfig = (updates: Partial<typeof config>) => {
    updateField('schedule_meeting_config', { ...config, ...updates })
  }

  const isCalendar = config.provider === 'google_calendar' || config.provider === 'outlook_calendar'
  const isCalendly = config.provider === 'calendly'

  return (
    <div className={sectionClass}>
      <Field label="Label">
        {(f) => (
          <input
            {...f}
            type="text"
            className={inputClass} style={inputStyle}
            value={data.label || ''}
            onChange={(e) => updateField('label', e.target.value)}
            placeholder="Schedule Meeting"
          />
        )}
      </Field>

      {/* Provider selector */}
      <Field label="Provider" as="group">
        <div className="grid grid-cols-2 gap-2">
          {PROVIDERS.map((p) => {
            const Icon = p.icon
            const isSelected = config.provider === p.value
            return (
              <button
                key={p.value}
                onClick={() => updateConfig({ provider: p.value })}
                className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border-2 text-left transition-all duration-150 ${
                  isSelected
                    ? 'border-[#5e6ad2]/40 bg-[#5e6ad2]/10 text-[#5e6ad2] dark:text-[#a5b4fc]'
                    : 'border-border/60 bg-card text-muted-foreground hover:border-border hover:bg-accent/30'
                }`}
              >
                <Icon className="w-4 h-4 flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-xs font-medium leading-tight">{p.label}</p>
                  <p className="text-[10px] opacity-70 leading-tight">{p.desc}</p>
                </div>
              </button>
            )
          })}
        </div>
      </Field>

      {/* Topic / Subject — not for Calendly */}
      {!isCalendly && (
        <Field
          label={isCalendar ? 'Event Subject' : 'Meeting Topic'}
          hint={'Use {{contact.field}} for dynamic values'}
        >
          {(f) => (
            <TemplateInput
              {...f}
              value={config.topic_template}
              onChange={(v) => updateConfig({ topic_template: v })}
              placeholder="{{contact.company_name}} — Discovery Call"
              rows={1}
            />
          )}
        </Field>
      )}

      {/* Duration — not for Calendly */}
      {!isCalendly && (
        <Field label="Duration (minutes)">
          {(f) => (
            <Select
              {...f}
              value={String(config.duration)}
              onChange={(value) => updateConfig({ duration: parseInt(value) })}
              options={DURATION_OPTIONS}
              className="w-full"
            />
          )}
        </Field>
      )}

      {/* Agenda / Description — not for Calendly */}
      {!isCalendly && (
        <Field label={isCalendar ? 'Description (optional)' : 'Agenda (optional)'}>
          {(f) => (
            <TemplateInput
              {...f}
              value={config.agenda_template}
              onChange={(v) => updateConfig({ agenda_template: v })}
              placeholder="Discuss partnership with {{contact.company_name}}"
              rows={2}
            />
          )}
        </Field>
      )}

      {/* Add contact as attendee — calendar providers only */}
      {isCalendar && (
        <label className={checkboxLabelClass}>
          <input
            type="checkbox"
            checked={config.include_contact_as_attendee}
            onChange={(e) => updateConfig({ include_contact_as_attendee: e.target.checked })}
            className={checkboxInputClass}
          />
          <span className="text-sm">Add contact as attendee</span>
        </label>
      )}

      {/* Google Calendar ID */}
      {config.provider === 'google_calendar' && (
        <Field label="Calendar ID" hint={'Use "primary" for default calendar'}>
          {(f) => (
            <input
              {...f}
              type="text"
              className={inputClass} style={inputStyle}
              value={config.calendar_id}
              onChange={(e) => updateConfig({ calendar_id: e.target.value })}
              placeholder="primary"
            />
          )}
        </Field>
      )}

      {/* Outlook User ID */}
      {config.provider === 'outlook_calendar' && (
        <Field label="User ID" hint={'Use "me" for the authenticated user'}>
          {(f) => (
            <input
              {...f}
              type="text"
              className={inputClass} style={inputStyle}
              value={config.outlook_user_id}
              onChange={(e) => updateConfig({ outlook_user_id: e.target.value })}
              placeholder="me"
            />
          )}
        </Field>
      )}

      <div className={infoBannerClass}>
        <p className="text-xs">
          {HELP_TEXT[config.provider]}
        </p>
      </div>
    </div>
  )
}
